import React, { useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Tooltip,
  Box
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Metric, EditedData } from '@features/deals/types';
import { formatCurrency, formatPercent } from '@utils/financialUtils';

interface MetricsTableProps {
  metrics: Metric[];
  tableYears: number[];
  editedData: EditedData;
  onValueChange: (index: number, category: string, year: string, value: string) => void;
  onDeleteItem: (index: number, category: string) => void;
}

// Helper function to determine if a category should be formatted as percentage
const isPercentCategory = (category: string): boolean => {
  const percentCategories = ['Occupancy Rate', 'occupancy', 'Profit Margin', 'margin'];
  return percentCategories.some(term => category.toLowerCase().includes(term.toLowerCase()));
};

const MetricsTable: React.FC<MetricsTableProps> = ({
  metrics,
  tableYears,
  editedData,
  onValueChange,
  onDeleteItem
}) => {
  // Add debug logging to check what data is being received
  useEffect(() => {
    console.log('MetricsTable received data:', {
      metricsCount: metrics.length,
      tableYearsCount: tableYears.length,
      metrics: metrics,
      years: tableYears
    });
  }, [metrics, tableYears]);

  // Group metrics by category-item
  const metricGroups = metrics.reduce<{
    [key: string]: {
      category: string;
      item: string;
      source?: 'confirmed' | 'manual' | 'ocr';
      isPercent: boolean;
      years: { [key: number]: string };
      sortOrder?: number;
      originalIndices: number[]; // Track original indices for editing
    };
  }>((acc, metric, index) => {
    // For debugging - log each metric as it's processed
    if (index < 5) {
      console.log(`Processing metric ${index}:`, metric);
    }

    // Create a unique key for each category-item combination
    const key = `${metric.category}${metric.item ? '-' + metric.item : ''}`;
    console.log(`Created key: ${key} for metric index ${index}`);

    if (!acc[key]) {
      // Determine if this is a percentage metric
      const isPercent = isPercentCategory(metric.category);

      // Determine sort order based on category for financial statement standard order
      let sortOrder = 999;
      if (metric.category.toLowerCase().includes('revenue')) sortOrder = 100;
      else if (metric.category.toLowerCase().includes('expense')) sortOrder = 200;
      else if (metric.category.toLowerCase().includes('noi') ||
               metric.category.toLowerCase().includes('net operating income')) sortOrder = 300;
      else if (metric.category.toLowerCase().includes('occupancy')) sortOrder = 400;
      else if (metric.category.toLowerCase().includes('adr')) sortOrder = 410;
      else if (metric.category.toLowerCase().includes('revpar')) sortOrder = 420;

      acc[key] = {
        category: metric.category,
        item: metric.item || '',
        source: metric.source,
        isPercent,
        years: {},
        sortOrder,
        originalIndices: [] // Initialize empty array for original indices
      };
    }

    // Add this metric's index to the originalIndices array
    acc[key].originalIndices.push(index);

    // Format value to string for display
    const formattedValue = typeof metric.value === 'string' ?
      metric.value :
      metric.value.toString();

    acc[key].years[metric.year] = formattedValue;

    // Log to debug
    console.log(`Added year ${metric.year} with value ${formattedValue} to key ${key}`);

    // If this metric has a different source (like 'confirmed'), prioritize it
    if (metric.source === 'confirmed') {
      acc[key].source = 'confirmed';
    } else if (metric.source === 'ocr' && acc[key].source !== 'confirmed') {
      acc[key].source = 'ocr';
    }

    return acc;
  }, {});

  // Log the grouped metrics for debugging
  console.log(`Grouped ${metrics.length} metrics into ${Object.keys(metricGroups).length} groups`);
  console.log('First few grouped metrics:', Object.entries(metricGroups).slice(0, 3));

  // Sort the metrics in standard financial statement order
  const sortedMetricGroups = Object.entries(metricGroups).sort((a, b) => {
    return (a[1].sortOrder || 999) - (b[1].sortOrder || 999);
  });

  return (
    <TableContainer
      component={Paper}
      elevation={2}
      sx={{
        borderRadius: 2,
        '& .MuiTableCell-root': {
          px: 2,
          py: 1,
          fontSize: '0.875rem',
        },
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                backgroundColor: 'primary.main',
                color: 'common.white',
                fontWeight: 600,
                width: '25%',
              }}
            >
              Category
            </TableCell>
            <TableCell
              sx={{
                backgroundColor: 'primary.main',
                color: 'common.white',
                fontWeight: 600,
                width: '15%',
              }}
            >
              Item
            </TableCell>
            {tableYears.map((year) => (
              <TableCell
                key={year}
                align="right"
                sx={{
                  backgroundColor: 'primary.main',
                  color: 'common.white',
                  fontWeight: 600,
                  width: `${50 / tableYears.length}%`,
                }}
              >
                {year}
              </TableCell>
            ))}
            <TableCell
              align="center"
              sx={{
                backgroundColor: 'primary.main',
                color: 'common.white',
                fontWeight: 600,
                width: '10%',
              }}
            >
              Source
            </TableCell>
            <TableCell
              align="center"
              sx={{
                backgroundColor: 'primary.main',
                color: 'common.white',
                fontWeight: 600,
                width: '5%',
              }}
            >
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedMetricGroups.map(([key, group], index) => (
            <TableRow
              key={key}
              hover
              sx={{
                '&:nth-of-type(odd)': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {group.category}
                  {group.isPercent && (
                    <Tooltip title="Percentage value (0-100)">
                      <InfoOutlinedIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary', fontSize: '0.875rem' }} />
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
              <TableCell>{group.item}</TableCell>
              {tableYears.map((year) => (
                <TableCell key={year} align="right">
                  {group.years[year] ? (
                    <TextField
                      value={
                        // Use the first original index to find edited data
                        group.originalIndices.length > 0 && editedData[group.originalIndices[0]]?.[group.category]?.[year.toString()] ||
                        group.years[year]
                      }
                      onChange={(e) => {
                        // Apply edit to all original indices for this group
                        group.originalIndices.forEach(index => {
                          onValueChange(index, group.category, year.toString(), e.target.value);
                        });
                      }}
                      disabled={group.source === 'confirmed'}
                      variant="standard"
                      fullWidth
                      inputProps={{
                        style: {
                          textAlign: 'right',
                          padding: '2px 0',
                        },
                      }}
                      sx={{
                        '& .MuiInput-underline:before': {
                          borderBottomColor: 'transparent',
                        },
                        '& .MuiInput-underline:hover:before': {
                          borderBottomColor: 'divider',
                        },
                      }}
                      InputProps={{
                        startAdornment: group.isPercent ? null : <InputAdornment position="start">$</InputAdornment>,
                        endAdornment: group.isPercent ? <InputAdornment position="end">%</InputAdornment> : null,
                      }}
                    />
                  ) : (
                    '-'
                  )}
                </TableCell>
              ))}
              <TableCell align="center">
                {group.source === 'confirmed' ? (
                  <Chip label="Confirmed" color="success" size="small" sx={{ fontSize: '0.7rem' }} />
                ) : group.source === 'ocr' ? (
                  <Chip label="OCR" color="primary" size="small" sx={{ fontSize: '0.7rem' }} />
                ) : (
                  <Chip label="Manual" color="default" size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                )}
              </TableCell>
              <TableCell align="center">
                {group.source !== 'confirmed' && (
                  <IconButton
                    onClick={() => {
                      // Delete all metrics in this group using their original indices
                      // Delete in reverse order to avoid index shifting issues
                      [...group.originalIndices].sort((a, b) => b - a).forEach(idx => {
                        onDeleteItem(idx, group.category);
                      });
                    }}
                    size="small"
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default MetricsTable;